import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import { useAppBar } from '../../providers/AppBarProvider';
import { fetchById, formatDateHour, handleError } from '../../utils/lib';
import { useConfig } from '../../providers/ConfigProvider';
import ViewPage from './ViewPage';
import BottomSheet from '../../layouts/BottomSheet';
import { useSnackbar } from "../../providers/SnackBarProvider";
import axios from 'axios';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import { useTranslation } from 'react-i18next';

function ViewAgendaEvent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [id, setId] = useState(location.state.id);
  const [agendaEvent, setAgendaEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const {updateTitle, toggleFab} = useAppBar();
  const {config, isLoadingConfig} = useConfig();
  const snackbar = useSnackbar();
  const { setNavigation } = useNavigationHelperStore();
  const { t } = useTranslation();

  const [deleteDialog, setDeleteDialog] = useState({
    open: false
  });

  const [completeDialog, setCompleteDialog] = useState({
    open: false
  });

  useEffect(() => { 
    setIsLoading(true);
    toggleFab(false);
    const fetchData = async () => {
      try {
        const agendaEvent = await fetchById(id, 'agenda');
        setAgendaEvent(agendaEvent);
        updateTitle('', agendaEvent.event_name);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleEdit = () => {
    setNavigation(location.pathname, { state: { id: agendaEvent.id } });
    navigate('/main/edit-agenda', { state: { updateAgendaFromLocation: agendaEvent } });
  };

  const handleDeleteOpen = () => {
    setDeleteDialog({
      open: true
    });
  };

  const handleDeleteClose = () => {
    setDeleteDialog({
      open: false
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}agenda?id=${id}`,
        { withCredentials: true }
      );
      
      handleDeleteClose();
      navigate(-1);
    } catch (error) {
      handleError(error, snackbar);
    }
  };

  const handleCompleteOpen = () => {
    setCompleteDialog({
      open: true
    });
  };

  const handleCompleteClose = () => {
    setCompleteDialog({
      open: false
    });
  };

  const handleComplete = async () => {
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}agenda?id=${id}&set_executed`,
        {},
        { withCredentials: true }
      );

      // Actualizar el evento después de completar
      const updatedEvent = await fetchById(id, 'agenda');
      setAgendaEvent(updatedEvent);
      handleCompleteClose();
    } catch (error) {
      handleError(error, snackbar);
    }
  };

  if (isLoading || isLoadingConfig || !agendaEvent) return (
    <>
      <CircularProgress
        size={42}
        sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
      />
    </>
  );

  return (
    <ViewPage isLoading={isLoading || isLoadingConfig}>
      {agendaEvent.pet && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{config?.vet?.pet_naming_es || t('main.pet')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {agendaEvent.pet.name}
          </Typography>
        </Box>
      )}

      {agendaEvent.owner && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('main.owner')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {agendaEvent.owner.name}
          </Typography>
        </Box>
      )}

      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('view.agendaEventServiceName')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {agendaEvent.event_name}
        </Typography>
      </Box>

      {agendaEvent.product && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('view.agendaEventProduct')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {agendaEvent.product.name} ({t('view.agendaEventDurationMinutes', { minutes: agendaEvent.product.service_duration })})
          </Typography>
        </Box>
      )}
      
      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('view.agendaEventStart')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {formatDateHour(agendaEvent.begin_time)}
        </Typography>
      </Box>

      {agendaEvent.end_time && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('view.agendaEventEnd')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {formatDateHour(agendaEvent.end_time)}
          </Typography>
        </Box>
      )}

      {agendaEvent.description && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.description')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {agendaEvent.description}
          </Typography>
        </Box>
      )}

      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('common.user')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {agendaEvent.user.name}
        </Typography>
      </Box>

      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('view.agendaEventExecutionStatus')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {agendaEvent.executed === 1 ? t('view.agendaEventCompleted') : t('view.agendaEventNotCompleted')}
        </Typography>
      </Box>

      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('view.agendaEventTaskVisibility')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {agendaEvent.private_task === 1 ? t('view.agendaEventPrivate') : t('view.agendaEventPublic')}
        </Typography>
      </Box>

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('view.agendaEventDeleteTitle')}</DialogTitle>
        <DialogContent>
          {t('view.agendaEventDeleteMessage')}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={completeDialog.open}
        onClose={handleCompleteClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('view.agendaEventCompleteTitle')}</DialogTitle>
        <DialogActions>
          <Button onClick={handleCompleteClose}>{t('common.cancel')}</Button>
          <Button onClick={handleComplete} variant="contained" color="primary">
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {agendaEvent.executed === 0 && (
        <BottomSheet 
          zIndex={1001}
          buttonGroups={[
            {
              label: t('common.actions'), 
              buttons: [
                ...(config.permissions.actions_permissions.agenda.update ? [{
                  label: t('view.markAsDone'),
                  action: handleCompleteOpen,
                  icon: <CheckIcon />
                }] : []),
              ].filter(Boolean)
            },
            {
              label: t('view.agendaEventAgendaGroup'),
              buttons: [
                ...(config.permissions.actions_permissions.agenda.update ? [{
                  label: t('view.vetActionEdit'),
                  action: handleEdit,
                  icon: <EditIcon />
                }] : []),
                ...(config.permissions.actions_permissions.agenda.delete ? [{
                  label: t('common.delete'),
                  action: handleDeleteOpen,
                  icon: <DeleteIcon />
                }] : []),
              ].filter(Boolean)
            },
          ]}
        />
      )}

    </ViewPage>
  );
}

export default ViewAgendaEvent;